import { useState } from "react";

// Loose typo check, same shape the server enforces. The confirm-field is the
// real defense: a wrong notification address = a silently-lost analysis.
const EMAIL_RE = /^\S+@\S+\.\S+$/;

/**
 * Change the address the "analysis ready" email goes to, with retype-to-confirm.
 * Presentational: the host (App) supplies `onSave`, which calls the run-scoped
 * /runs/notify-email endpoint. Save stays disabled until both fields match a
 * valid, changed address — so a mistyped inbox can't strand the result.
 */
export function ChangeEmailModal({
  current,
  onSave,
  onClose,
}: {
  current: string;
  onSave: (email: string) => Promise<void>;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(current);
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = email.trim();
  const matches =
    trimmed.length > 0 && trimmed.toLowerCase() === confirm.trim().toLowerCase();
  const valid = EMAIL_RE.test(trimmed);
  const changed = trimmed.toLowerCase() !== current.trim().toLowerCase();
  const canSave = matches && valid && changed && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed);
      onClose();
    } catch {
      setError("We couldn't update that — check the address and try again.");
      setSaving(false);
    }
  };

  return (
    <div
      className="cem-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Change the notification email"
      // NO backdrop-click dismiss: this is a data-entry modal, and a stray
      // click outside silently threw away a half-typed address (Rich, live
      // 2026-06-12). It closes only on Save or the explicit Cancel button.
    >
      <div className="cem-modal">
        <h2 className="cem-title">Where should we send the link?</h2>
        <p className="cem-lede">
          We’ll email you here when your analysis is ready. Make sure it’s an
          inbox only you can see.
        </p>
        <label className="cem-field">
          <span>Email</span>
          <input
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="cem-field">
          <span>Confirm email</span>
          <input
            type="email"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void save();
            }}
            className={confirm.length > 0 && matches ? "ok" : undefined}
          />
          {confirm.length > 0 &&
            (matches ? (
              <span className="cem-hint good">✓ Matches</span>
            ) : (
              <span className="cem-hint">Doesn’t match yet</span>
            ))}
        </label>
        {error && <div className="cem-error">{error}</div>}
        <div className="cem-row">
          <button type="button" className="cem-btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="cem-btn solid"
            disabled={!canSave}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
